import { Alert, Badge, Button, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, GitCompare, History, MessageSquare, RotateCcw, Send, Upload } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../../api/client';
import { ErreurApi, lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { ErreurChargement, Select } from '../../components/bits';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { TabPanel, Tabs } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { formatDateTime, formatNombre } from '../../lib/format';
import { PageHeader } from '../../pages/PageHeader';

import { LIBELLE_SOURCE, useCoach, useEcritureContenus, type Coach } from './catalogue';
import { blocsDiff, diffLignes, resumeDiff } from './diff';

type Version = components['schemas']['PromptVersionSummary'];
type OngletPanneau = 'versions' | 'comparer' | 'essai';

const LONGUEUR_MAX = 100_000;

interface Brouillon {
  texte: string;
  /** Version en service quand le brouillon a commencé (contrôle des publications concurrentes). */
  baseVersionId: string | null;
  enregistreLe: string;
}

const cleBrouillon = (coachId: string) => `mbg-admin.prompt.${coachId}`;

export function lireBrouillon(coachId: string): Brouillon | null {
  try {
    const brut = localStorage.getItem(cleBrouillon(coachId));
    return brut ? (JSON.parse(brut) as Brouillon) : null;
  } catch {
    return null;
  }
}

function ecrireBrouillon(coachId: string, b: Brouillon | null): void {
  try {
    if (b) localStorage.setItem(cleBrouillon(coachId), JSON.stringify(b));
    else localStorage.removeItem(cleBrouillon(coachId));
  } catch {
    // Stockage indisponible : le brouillon vit le temps de la page.
  }
}

function useVersions(coachId: string) {
  return useQuery({
    queryKey: ['prompt-versions', coachId],
    queryFn: async () => (await lire(api.GET('/api/admin/catalog/coaches/{id}/prompt-versions', { params: { path: { id: coachId } } }))).versions,
  });
}

/** Texte d'une version (mis en cache : une version ne change jamais). */
function requeteTexte(coachId: string, versionId: string) {
  return {
    queryKey: ['prompt-version', versionId],
    queryFn: async () =>
      (await lire(api.GET('/api/admin/catalog/coaches/{id}/prompt-versions/{versionId}', { params: { path: { id: coachId, versionId } } }))).version.systemPrompt,
    staleTime: Infinity,
  };
}

/**
 * Éditeur de prompt plein écran (cahier des charges §7.7) : brouillon gardé
 * sur cet ordinateur, publication qui crée une version, historique,
 * comparaison de deux versions, restauration, conversation d'essai.
 */
export function PromptEditor() {
  const { id = '' } = useParams();
  const coach = useCoach(id);
  const c = coach.data;

  if (coach.isError) return <ErreurChargement erreur={coach.error} onReessayer={() => void coach.refetch()} />;
  if (!c) {
    return (
      <div className="stack">
        <Skeleton height={48} />
        <Skeleton height={420} />
      </div>
    );
  }
  return <Editeur key={c.id} coach={c} />;
}

function Editeur({ coach }: { coach: Coach }) {
  const ecriture = useEcritureContenus();
  const toast = useToast();
  const client = useQueryClient();
  const versions = useVersions(coach.id);
  const enService = coach.currentVersion?.id ?? null;

  const [brouillon, setBrouillon] = useState<Brouillon>(() => {
    const garde = lireBrouillon(coach.id);
    return garde && garde.texte !== coach.systemPrompt
      ? garde
      : { texte: coach.systemPrompt, baseVersionId: enService, enregistreLe: new Date().toISOString() };
  });
  const [repris, setRepris] = useState(() => {
    const garde = lireBrouillon(coach.id);
    return garde && garde.texte !== coach.systemPrompt ? garde : null;
  });
  const [note, setNote] = useState('');
  const [onglet, setOnglet] = useState<OngletPanneau>('versions');
  const [comparaison, setComparaison] = useState<{ a: string; b: string }>({ a: 'service', b: 'brouillon' });
  const [publier, setPublier] = useState(false);
  const [conflit, setConflit] = useState(false);

  const modifie = brouillon.texte !== coach.systemPrompt;
  const depasse = !!brouillon.baseVersionId && !!enService && brouillon.baseVersionId !== enService;

  // Prompt en service changé (publication, restauration) : un brouillon resté
  // identique à l'ancien texte suit le nouveau, et repart de la nouvelle version.
  const precedent = useRef(coach.systemPrompt);
  useEffect(() => {
    const avant = precedent.current;
    precedent.current = coach.systemPrompt;
    setBrouillon((b) =>
      b.texte === coach.systemPrompt || b.texte === avant ? { texte: coach.systemPrompt, baseVersionId: enService, enregistreLe: b.enregistreLe } : b
    );
  }, [coach.systemPrompt, enService]);

  // Brouillon enregistré sur cet ordinateur, 500 ms après la dernière frappe.
  useEffect(() => {
    const t = window.setTimeout(() => ecrireBrouillon(coach.id, modifie ? brouillon : null), 500);
    return () => window.clearTimeout(t);
  }, [brouillon, modifie, coach.id]);

  const resume = useMemo(() => (modifie ? resumeDiff(diffLignes(coach.systemPrompt, brouillon.texte)) : null), [modifie, coach.systemPrompt, brouillon.texte]);

  const rafraichir = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['coach', coach.id] }),
      client.invalidateQueries({ queryKey: ['prompt-versions', coach.id] }),
      client.invalidateQueries({ queryKey: ['catalogue'] }),
      client.invalidateQueries({ queryKey: ['audit', coach.id] }),
    ]);
  };

  const envoyer = async (base: string | null) => {
    try {
      await lire(
        api.PUT('/api/admin/catalog/coaches/{id}/prompt', {
          params: { path: { id: coach.id } },
          body: { systemPrompt: brouillon.texte, ...(note.trim() ? { note: note.trim() } : {}), ...(base ? { baseVersionId: base } : {}) },
        })
      );
    } catch (err) {
      if (err instanceof ErreurApi && err.code === 'PROMPT_CHANGED') {
        setConflit(true);
        await rafraichir();
        return;
      }
      throw err;
    }
    ecrireBrouillon(coach.id, null);
    setNote('');
    setConflit(false);
    setRepris(null);
    toast.succes('Prompt publié : les prochaines réponses du coach l’utilisent.');
    await rafraichir();
  };

  const chargerTexte = (texte: string, versionId: string | null) => {
    setRepris(null);
    setBrouillon({ texte, baseVersionId: enService ?? versionId, enregistreLe: new Date().toISOString() });
  };

  return (
    <>
      <PageHeader
        title={`Prompt · ${coach.name}`}
        lead={
          <>
            Module « {coach.module.name} » ·{' '}
            {coach.currentVersion
              ? `en service depuis le ${formatDateTime(coach.currentVersion.createdAt)} (${coach.currentVersion.authorEmail ?? LIBELLE_SOURCE[coach.currentVersion.source]})`
              : 'aucune version enregistrée'}
            {!coach.isActive && ' · coach inactif'}
          </>
        }
        actions={
          <>
            <Link className="ui-btn ui-btn--ghost lien-bouton" to="/prompts">
              <ArrowLeft size={15} aria-hidden="true" /> Tous les prompts
            </Link>
            <Link className="ui-btn ui-btn--secondary lien-bouton" to={`/coachs/${coach.id}`}>
              Fiche du coach
            </Link>
          </>
        }
      />

      {repris && modifie && (
        <Alert tone="info">
          Brouillon non publié du {formatDateTime(repris.enregistreLe)} repris sur cet ordinateur.{' '}
          <button type="button" className="lien-discret" onClick={() => chargerTexte(coach.systemPrompt, enService)}>
            Revenir à la version en service
          </button>
        </Alert>
      )}
      {(depasse || conflit) && modifie && (
        <Alert>
          Une autre version a été publiée depuis le début de ce brouillon. Comparez-la avant de publier, pour ne pas effacer le travail d’un
          collègue.{' '}
          <button
            type="button"
            className="lien-discret"
            onClick={() => {
              setComparaison({ a: 'service', b: 'brouillon' });
              setOnglet('comparer');
            }}
          >
            Comparer
          </button>
        </Alert>
      )}

      <div className="editeur">
        <div className="editeur__zone">
          <label htmlFor="prompt-texte" className="sr-only">
            Texte du prompt
          </label>
          <textarea
            id="prompt-texte"
            className="ui-input editeur__texte"
            value={brouillon.texte}
            readOnly={!ecriture}
            spellCheck={false}
            onChange={(e) => setBrouillon((b) => ({ ...b, texte: e.target.value, enregistreLe: new Date().toISOString() }))}
          />
          <div className="editeur__etat">
            <span className={brouillon.texte.length > LONGUEUR_MAX ? 'neg' : undefined}>
              {formatNombre(brouillon.texte.length)} caractères
              {brouillon.texte.length > LONGUEUR_MAX && ` (limite ${formatNombre(LONGUEUR_MAX)})`}
            </span>
            {modifie && resume ? (
              <span>
                Brouillon : <span className="diff-plus">+{resume.ajoutees}</span> <span className="diff-moins">−{resume.retirees}</span> lignes par
                rapport à la version en service · gardé sur cet ordinateur
              </span>
            ) : (
              <span className="muted">Identique à la version en service</span>
            )}
          </div>
          {ecriture && (
            <div className="editeur__publier">
              <Input
                aria-label="Note de version"
                placeholder="Note de version (facultatif) : ce qui change et pourquoi"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={300}
              />
              <Button
                variant="primary"
                icon={<Upload size={15} aria-hidden="true" />}
                disabled={!modifie || brouillon.texte.trim().length < 10 || brouillon.texte.length > LONGUEUR_MAX}
                onClick={() => setPublier(true)}
              >
                Publier
              </Button>
            </div>
          )}
        </div>

        <aside className="editeur__panneau" aria-label="Versions, comparaison et essai">
          <Tabs
            label="Outils du prompt"
            onglets={[
              { id: 'versions', label: 'Versions', count: versions.data?.length ?? null },
              { id: 'comparer', label: 'Comparer' },
              ...(ecriture ? [{ id: 'essai' as const, label: 'Essai' }] : []),
            ]}
            actif={onglet}
            onChange={setOnglet}
          />
          <TabPanel label={onglet === 'versions' ? 'Versions' : onglet === 'comparer' ? 'Comparer' : 'Essai'}>
            {onglet === 'versions' && (
              <ListeVersions
                coach={coach}
                versions={versions}
                onComparer={(vid) => {
                  setComparaison({ a: vid, b: modifie ? 'brouillon' : 'service' });
                  setOnglet('comparer');
                }}
                onCharger={chargerTexte}
                onRestauree={rafraichir}
              />
            )}
            {onglet === 'comparer' && (
              <Comparaison coach={coach} versions={versions.data ?? []} brouillon={modifie ? brouillon.texte : null} choix={comparaison} onChoix={setComparaison} />
            )}
            {onglet === 'essai' && ecriture && <Essai coach={coach} brouillon={modifie ? brouillon.texte : null} />}
          </TabPanel>
        </aside>
      </div>

      <ConfirmDialog
        open={publier}
        onOpenChange={setPublier}
        title="Publier le prompt"
        confirmLabel={conflit || depasse ? 'Publier quand même' : 'Publier'}
        onConfirm={() => envoyer(conflit || depasse ? enService : brouillon.baseVersionId)}
      >
        <p>
          Les prochaines réponses de « {coach.name} » utiliseront ce texte ({resume ? `+${resume.ajoutees} −${resume.retirees} lignes` : 'modifié'}). Une
          nouvelle version est créée : l’actuelle reste restaurable.
        </p>
        {(conflit || depasse) && <p>Une autre version a été publiée depuis le début de ce brouillon : elle sera remplacée.</p>}
        <p className="muted small">Pensez à l’essayer dans l’onglet « Essai » avant de publier.</p>
      </ConfirmDialog>
    </>
  );
}

function ListeVersions({
  coach,
  versions,
  onComparer,
  onCharger,
  onRestauree,
}: {
  coach: Coach;
  versions: ReturnType<typeof useVersions>;
  onComparer: (versionId: string) => void;
  onCharger: (texte: string, versionId: string) => void;
  onRestauree: () => Promise<void>;
}) {
  const ecriture = useEcritureContenus();
  const toast = useToast();
  const client = useQueryClient();
  const [vue, setVue] = useState<Version | null>(null);
  const [restaurer, setRestaurer] = useState<Version | null>(null);

  if (versions.isError) return <ErreurChargement erreur={versions.error} onReessayer={() => void versions.refetch()} />;
  if (!versions.data) return <Skeleton height={200} />;
  if (versions.data.length === 0) return <EmptyState icon={<History size={24} aria-hidden="true" />} title="Aucune version" />;

  const charger = async (v: Version) => {
    try {
      onCharger(await client.fetchQuery(requeteTexte(coach.id, v.id)), v.id);
      toast.succes('Version chargée dans l’éditeur (non publiée).');
    } catch (err) {
      toast.erreur(messageDe(err));
    }
  };

  return (
    <>
      <ol className="versions">
        {versions.data.map((v) => (
          <li key={v.id} className={v.current ? 'versions__item versions__item--courante' : 'versions__item'}>
            <div className="versions__entete">
              <strong className="nowrap">{formatDateTime(v.createdAt)}</strong>
              {v.current && <Badge tone="success">En service</Badge>}
              {v.source !== 'backoffice' && <Badge>{LIBELLE_SOURCE[v.source]}</Badge>}
            </div>
            <div className="muted small">
              {v.authorEmail ?? 'Auteur inconnu'} · {formatNombre(v.length)} caractères
            </div>
            {v.note && <div className="versions__note">« {v.note} »</div>}
            <div className="versions__actions">
              <button type="button" className="lien-discret" onClick={() => setVue(v)}>
                Lire
              </button>
              {!v.current && (
                <button type="button" className="lien-discret" onClick={() => onComparer(v.id)}>
                  Comparer
                </button>
              )}
              {ecriture && !v.current && (
                <>
                  <button type="button" className="lien-discret" onClick={() => void charger(v)}>
                    Charger dans l’éditeur
                  </button>
                  <button type="button" className="lien-discret" onClick={() => setRestaurer(v)}>
                    Restaurer
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ol>
      {vue && <LectureVersion coachId={coach.id} version={vue} onClose={() => setVue(null)} />}
      <ConfirmDialog
        open={!!restaurer}
        onOpenChange={(o) => !o && setRestaurer(null)}
        title="Restaurer cette version"
        confirmLabel="Restaurer et publier"
        onConfirm={async () => {
          if (!restaurer) return;
          await lire(
            api.POST('/api/admin/catalog/coaches/{id}/prompt-versions/{versionId}/restore', {
              params: { path: { id: coach.id, versionId: restaurer.id } },
              body: {},
            })
          );
          toast.succes('Version restaurée : elle est de nouveau en service.');
          await onRestauree();
        }}
      >
        {restaurer && (
          <p>
            La version du {formatDateTime(restaurer.createdAt)} redevient celle du coach, tout de suite. Une version « Restauration » est ajoutée à
            l’historique ; l’actuelle reste restaurable.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

function LectureVersion({ coachId, version, onClose }: { coachId: string; version: Version; onClose: () => void }) {
  const texte = useQuery(requeteTexte(coachId, version.id));
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Version du ${formatDateTime(version.createdAt)}`} width={820}>
      {texte.isError ? (
        <ErreurChargement erreur={texte.error} onReessayer={() => void texte.refetch()} />
      ) : texte.data === undefined ? (
        <Skeleton height={300} />
      ) : (
        <pre className="apercu-prompt apercu-prompt--grand">{texte.data}</pre>
      )}
    </Modal>
  );
}

/** Comparaison ligne à ligne de deux versions (ou du brouillon). */
function Comparaison({
  coach,
  versions,
  brouillon,
  choix,
  onChoix,
}: {
  coach: Coach;
  versions: Version[];
  brouillon: string | null;
  choix: { a: string; b: string };
  onChoix: (c: { a: string; b: string }) => void;
}) {
  const id = useId();
  const options = [
    ...(brouillon !== null ? [{ id: 'brouillon', libelle: 'Brouillon (non publié)' }] : []),
    { id: 'service', libelle: 'Version en service' },
    ...versions.filter((v) => !v.current).map((v) => ({ id: v.id, libelle: `${formatDateTime(v.createdAt)}${v.note ? ` · ${v.note.slice(0, 40)}` : ''}` })),
  ];
  const a = options.some((o) => o.id === choix.a) ? choix.a : 'service';
  const b = options.some((o) => o.id === choix.b) ? choix.b : (brouillon !== null ? 'brouillon' : 'service');
  const aCharger = [a, b].filter((x) => x !== 'service' && x !== 'brouillon');
  const textes = useQueries({ queries: aCharger.map((vid) => requeteTexte(coach.id, vid)) });

  const texte = (x: string): string | undefined => {
    if (x === 'service') return coach.systemPrompt;
    if (x === 'brouillon') return brouillon ?? coach.systemPrompt;
    return textes[aCharger.indexOf(x)]?.data;
  };
  const ta = texte(a);
  const tb = texte(b);
  const lignes = useMemo(() => (ta !== undefined && tb !== undefined ? diffLignes(ta, tb) : null), [ta, tb]);
  const erreur = textes.find((t) => t.isError)?.error;

  return (
    <div className="stack">
      <div className="grille-2">
        <Field id={`${id}-a`} label="Avant">
          <Select id={`${id}-a`} value={a} onChange={(e) => onChoix({ a: e.target.value, b })}>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.libelle}
              </option>
            ))}
          </Select>
        </Field>
        <Field id={`${id}-b`} label="Après">
          <Select id={`${id}-b`} value={b} onChange={(e) => onChoix({ a, b: e.target.value })}>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.libelle}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {erreur ? <Alert>{messageDe(erreur)}</Alert> : !lignes ? <Skeleton height={200} /> : <VueDiff lignes={lignes} />}
    </div>
  );
}

function VueDiff({ lignes }: { lignes: ReturnType<typeof diffLignes> }) {
  const { ajoutees, retirees } = resumeDiff(lignes);
  const { blocs, masqueesFin } = blocsDiff(lignes);
  if (!ajoutees && !retirees) return <p className="texte-aide">Les deux textes sont identiques.</p>;
  return (
    <div className="diff" role="region" aria-label="Différences">
      <p className="diff__resume">
        <GitCompare size={14} aria-hidden="true" /> <span className="diff-plus">{ajoutees} ligne{ajoutees > 1 ? 's' : ''} ajoutée{ajoutees > 1 ? 's' : ''}</span>
        {' · '}
        <span className="diff-moins">
          {retirees} retirée{retirees > 1 ? 's' : ''}
        </span>
      </p>
      {blocs.map((bloc, i) => (
        <div key={i}>
          {bloc.masquees > 0 && <div className="diff__masquees">… {bloc.masquees} ligne{bloc.masquees > 1 ? 's' : ''} identique{bloc.masquees > 1 ? 's' : ''}</div>}
          {bloc.lignes.map((l, k) => (
            <div key={k} className={`diff__ligne diff__ligne--${l.type}`}>
              <span className="diff__num" aria-hidden="true">
                {l.type === 'ajoutee' ? l.apres : l.avant}
              </span>
              <span className="diff__signe" aria-label={l.type === 'ajoutee' ? 'ajoutée' : l.type === 'retiree' ? 'retirée' : undefined}>
                {l.type === 'ajoutee' ? '+' : l.type === 'retiree' ? '−' : ' '}
              </span>
              <span className="diff__texte">{l.texte || ' '}</span>
            </div>
          ))}
        </div>
      ))}
      {masqueesFin > 0 && <div className="diff__masquees">… {masqueesFin} ligne{masqueesFin > 1 ? 's' : ''} identique{masqueesFin > 1 ? 's' : ''}</div>}
    </div>
  );
}

interface Echange {
  role: 'user' | 'assistant';
  content: string;
  modele?: string;
  duree?: number;
}

/** Conversation d'essai : rien n'est enregistré, aucun crédit n'est débité. */
function Essai({ coach, brouillon }: { coach: Coach; brouillon: string | null }) {
  const [source, setSource] = useState<'brouillon' | 'service'>(brouillon !== null ? 'brouillon' : 'service');
  const [echanges, setEchanges] = useState<Echange[]>([]);
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const fin = useRef<HTMLDivElement>(null);
  const prompt = source === 'brouillon' && brouillon !== null ? brouillon : null;

  useEffect(() => {
    fin.current?.scrollIntoView?.({ block: 'nearest' });
  }, [echanges.length, envoi]);

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    const texte = saisie.trim();
    if (!texte || envoi) return;
    const suite: Echange[] = [...echanges, { role: 'user', content: texte }];
    setEchanges(suite);
    setSaisie('');
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await lire(
        api.POST('/api/admin/catalog/coaches/{id}/test', {
          params: { path: { id: coach.id } },
          body: { ...(prompt ? { systemPrompt: prompt } : {}), messages: suite.slice(-30).map(({ role, content }) => ({ role, content })) },
        })
      );
      setEchanges([...suite, { role: 'assistant', content: r.reply, modele: r.model, duree: r.durationMs }]);
    } catch (err) {
      setErreur(messageDe(err));
      setEchanges(echanges);
      setSaisie(texte);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="essai">
      <div className="row row--between">
        <div className="segmented" role="group" aria-label="Prompt essayé">
          <button type="button" aria-pressed={source === 'brouillon'} disabled={brouillon === null} onClick={() => setSource('brouillon')}>
            Brouillon
          </button>
          <button type="button" aria-pressed={source === 'service'} onClick={() => setSource('service')}>
            En service
          </button>
        </div>
        <Button size="sm" variant="ghost" icon={<RotateCcw size={13} aria-hidden="true" />} disabled={!echanges.length || envoi} onClick={() => setEchanges([])}>
          Recommencer
        </Button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Rien n’est enregistré ni facturé. Sans profil ni mémoire d’élève : le coach répond avec son seul prompt.
      </p>
      <div className="essai__fil" aria-live="polite">
        {echanges.length === 0 && (
          <EmptyState icon={<MessageSquare size={22} aria-hidden="true" />} title="Posez une question comme une élève" text="Ex. : « Je veux lancer une marque de cosmétiques, par où commencer ? »" />
        )}
        {echanges.map((m, i) => (
          <div key={i} className={`fil__message fil__message--${m.role === 'user' ? 'user' : 'assistant'}`}>
            <div className="fil__auteur">
              {m.role === 'user' ? 'Vous (élève fictive)' : coach.name}
              {m.modele && (
                <span className="fil__date">
                  {m.modele} · {(m.duree! / 1000).toFixed(1)} s
                </span>
              )}
            </div>
            <div className="fil__texte">{m.content}</div>
          </div>
        ))}
        {envoi && <div className="fil__message fil__message--assistant muted small">{coach.name} écrit…</div>}
        <div ref={fin} />
      </div>
      {erreur && <Alert>{erreur}</Alert>}
      <form className="essai__saisie" onSubmit={envoyer}>
        <textarea
          className="ui-input textarea"
          rows={2}
          value={saisie}
          aria-label="Message de l’élève fictive"
          placeholder="Votre message"
          maxLength={8000}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <Button type="submit" variant="primary" icon={<Send size={14} aria-hidden="true" />} loading={envoi} disabled={!saisie.trim()} aria-label="Envoyer">
          Envoyer
        </Button>
      </form>
    </div>
  );
}
